import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Form, FormInstance, Input } from 'antd';
import { RcFile } from 'antd/es/upload';
import React, { useCallback, useEffect } from 'react';
import { File as ApiFile, Capability } from '../../graphql/schema';
import TaskPromptUpload from './TaskPromptUpload';

export interface TaskPromptFormFields {
  prompt: string;
  files: (ApiFile | RcFile)[];
}

interface Props {
  form: FormInstance<TaskPromptFormFields>;
  onSend: (prompt: TaskPromptFormFields) => Promise<void>;
  loading?: boolean;
  capability?: Capability;
  focusOnLoad?: boolean;
  onChange?: (prompt: TaskPromptFormFields) => Promise<void>;
  standalone?: boolean;
}

const TaskPrompt = ({
  onSend,
  loading,
  form,
  capability,
  focusOnLoad,
  onChange,
  standalone,
}: Props) => {
  const handleSend = useCallback(
    async (formValues: TaskPromptFormFields) => {
      if (loading) return;
      form.resetFields();
      await onSend(formValues);
    },
    [onSend, loading, form],
  );

  useEffect(() => {
    if (focusOnLoad) {
      setTimeout(() => {
        document.getElementById('promptTextField')?.focus();
      }, 10);
    }
  }, [focusOnLoad]);

  const scrollToBottom = useCallback(() => {
    window.document.getElementById('messagesContainer')?.scrollTo({
      top: 999999999,
    });
    if (onChange) onChange(form.getFieldsValue());
  }, [form, onChange]);

  return (
    <div className="w-full justify-center flex lg:mb-3">
      <Form
        form={form}
        onFinish={handleSend}
        className={`flex flex-col pt-3 relative rounded-t-2xl ${standalone ? 'rounded-2xl' : ''} lg:rounded-2xl bg-white shadow-xl border border-gray-100 p-2 w-full md:max-w-3xl lg:max-w-[40rem] xl:max-w-[48rem]`}
        onChange={scrollToBottom}
      >
        <Form.Item name="prompt" required noStyle>
          <Input.TextArea
            className="border-0 bg-white outline-0 focus:outline-0 focus:border-none focus:shadow-none focus-within:shadow-none"
            placeholder="Wie kann ich helfen?"
            id="promptTextField"
            autoSize={{ minRows: 2, maxRows: 15 }}
            onKeyDown={(e) => {
              if (e.keyCode === 13 && !e.shiftKey) {
                form.submit();
              }
            }}
            readOnly={loading}
          />
        </Form.Item>
        <div className="flex flex-row justify-between items-end">
          <Form.Item name="files" noStyle>
            <TaskPromptUpload capability={capability} />
          </Form.Item>
          <Button
            htmlType="submit"
            className="rounded-full border-0 h-[35px] !w-[35px] !bg-[var(--theme-primary)] !text-[var(--theme-primary-text)] hover:ring hover:ring-gray-300"
            loading={loading}
          >
            {!loading && <FontAwesomeIcon icon={faArrowRight} />}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TaskPrompt;
